import aiLogger from './ai-logger';
import { Component } from "./component";
import MarkdownIt from "markdown-it";
import hljs from 'highlight.js/lib/core';
import hljsBash from 'highlight.js/lib/languages/bash';
import hljsCss from 'highlight.js/lib/languages/css';
import hljsDockerfile from 'highlight.js/lib/languages/dockerfile';
import hljsJavascript from 'highlight.js/lib/languages/javascript';
import hljsJson from 'highlight.js/lib/languages/json';
import hljsPhp from 'highlight.js/lib/languages/php';
import hljsPython from 'highlight.js/lib/languages/python';
import hljsSql from 'highlight.js/lib/languages/sql';
import hljsTypescript from 'highlight.js/lib/languages/typescript';
import hljsXml from 'highlight.js/lib/languages/xml';
import hljsYaml from 'highlight.js/lib/languages/yaml';

hljs.registerLanguage('bash', hljsBash);
hljs.registerLanguage('sh', hljsBash);
hljs.registerLanguage('shell', hljsBash);
hljs.registerLanguage('css', hljsCss);
hljs.registerLanguage('dockerfile', hljsDockerfile);
hljs.registerLanguage('javascript', hljsJavascript);
hljs.registerLanguage('js', hljsJavascript);
hljs.registerLanguage('json', hljsJson);
hljs.registerLanguage('php', hljsPhp);
hljs.registerLanguage('python', hljsPython);
hljs.registerLanguage('py', hljsPython);
hljs.registerLanguage('sql', hljsSql);
hljs.registerLanguage('typescript', hljsTypescript);
hljs.registerLanguage('ts', hljsTypescript);
hljs.registerLanguage('xml', hljsXml);
hljs.registerLanguage('html', hljsXml);
hljs.registerLanguage('yaml', hljsYaml);
hljs.registerLanguage('yml', hljsYaml);
import { AskQuestionsModal } from "./ask-questions-modal";
import { getCsrfToken } from '../services/ai-editor-utils';
import { languageMap, terminalLanguages, getFileIcon } from './chat-file-icons';
import { AiThinkingManager, ToolProgressPayload } from './AiThinkingManager';
import { ArtifactRenderer } from '../artifacts/ArtifactRenderer';
import { readSSEStream } from '../services/ai-stream-reader';
import { ChatFileUploadService } from '../services/ChatFileUploadService';
import { ChatConversationService } from '../services/ChatConversationService';

declare global {
    interface Window {
        currentUser?: {
            avatar_url: string;
            name: string;
        };
        $http: {
            post: (url: string, data: any) => Promise<any>;
            get: (url: string) => Promise<any>;
        };
        $events: {
            success: (message: string) => void;
            error: (message: string) => void;
        };
    }
}

interface Attachment {
    id: number;
    name: string;
    path: string;
    type: string;
    url: string;
}

interface HistoryMessage {
    id: number;
    conversation_id?: number;
    query: string;
    response: string;
    is_liked: boolean;
    is_disliked?: boolean;
    has_error: boolean;
    attachments: Attachment[];
}

export class AiChat extends Component {
    private form!: HTMLFormElement;
    private input!: HTMLElement;
    private body!: HTMLDivElement;
    private messageTemplate!: HTMLTemplateElement;
    private typingIndicatorTemplate!: HTMLTemplateElement;
    private historyLoadingTemplate!: HTMLTemplateElement;
    private stopButton!: HTMLButtonElement;
    private sendButton!: HTMLButtonElement;
    private initialLoading!: HTMLDivElement;

    private fileInput!: HTMLInputElement;
    private attachBtn!: HTMLButtonElement;
    private previewContainer!: HTMLDivElement;
    private footerContainer!: HTMLDivElement;

    private md: MarkdownIt;
    private abortController: AbortController | null = null;

    private userAvatar: string = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';
    private aiAvatar: string = '/athena-chat.png';
    private aiName: string = 'Athena';
    private userName: string = 'Você';

    private isLoadingHistory: boolean = false;
    private isFilesLoading: boolean = false;
    private isStreaming: boolean = false;
    private hasMoreHistory: boolean = false;
    private firstMessageId: number | null = null;
    private listenersRegistered: boolean = false;

    private selectedFiles: File[] = [];
    private dragEnterCounter: number = 0;
    private dropOverlay: HTMLDivElement | null = null;

    private userLoggedIn: boolean = false;
    private loginUrl: string = '/login';
    private loginPromptShown: boolean = false;
    private sessionCookieName: string = 'athena_session_id';

    private currentPromptId: string = 'athena-default';
    private currentPromptPlaceholder: string = 'Digite sua pergunta...';
    private prompts: any[] = [];
    private promptDropdown!: HTMLDivElement;
    private promptButton!: HTMLButtonElement;
    private isPromptDropdownOpen: boolean = false;

    private currentConversationId: number | null = null;
    private sidebar: any = null;
    /** ID da conversa que estava em streaming quando o usuário trocou de conversa. */
    private prevStreamingConversationId: number | null = null;
    /** True quando o abort foi iniciado por troca de conversa (não pelo botão Stop do usuário). */
    private abortedBySwitch: boolean = false;

    /** Auto-scroll "grudado" no fundo; false enquanto o usuário rola para cima durante o stream. */
    private autoScrollPinned: boolean = true;
    private jumpBottomBtn: HTMLButtonElement | null = null;

    /** Debounce da gravação de rascunho por conversa. */
    private draftSaveTimer: number | null = null;
    private askQuestionsModalInstance: AskQuestionsModal | null = null;

    private thinkingManager!: AiThinkingManager;

    constructor() {
        super();
        this.md = new MarkdownIt({
            html: false,
            linkify: true,
            breaks: true,
            highlight: (str: string, lang: string): string => {
                if (lang && hljs.getLanguage(lang)) {
                    try {
                        return hljs.highlight(str, { language: lang, ignoreIllegals: true }).value;
                    } catch (e) { /* fallback abaixo */ }
                }
                return ''; // markdown-it escapa o conteúdo quando retorna vazio
            },
        });

        const defaultRender = this.md.renderer.rules.link_open || function (tokens, idx, options, env, self) {
            return self.renderToken(tokens, idx, options);
        };

        this.md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
            const hrefIndex = tokens[idx].attrIndex('href');
            let isDocLink = false;
            let targetUrl = '';

            if (hrefIndex >= 0) {
                targetUrl = tokens[idx].attrs![hrefIndex][1];
                if (targetUrl.includes('/books/') && targetUrl.includes('/page/')) {
                    isDocLink = true;
                }
            }

            if (isDocLink) {
                const btnClasses = 'chat-doc-modal-btn';
                const classIndex = tokens[idx].attrIndex('class');
                if (classIndex < 0) {
                    tokens[idx].attrPush(['class', btnClasses]);
                } else {
                    tokens[idx].attrs![classIndex][1] += ' ' + btnClasses;
                }

                tokens[idx].attrPush(['data-action', 'open-doc-modal']);
                tokens[idx].attrPush(['data-url', targetUrl]);

                return defaultRender(tokens, idx, options, env, self);
            }

            const aIndex = tokens[idx].attrIndex('target');
            if (aIndex < 0) {
                tokens[idx].attrPush(['target', '_blank']);
            } else {
                tokens[idx].attrs![aIndex][1] = '_blank';
            }

            const relIndex = tokens[idx].attrIndex('rel');
            if (relIndex < 0) {
                tokens[idx].attrPush(['rel', 'noopener noreferrer']);
            } else {
                tokens[idx].attrs![relIndex][1] = 'noopener noreferrer';
            }

            return defaultRender(tokens, idx, options, env, self);
        };
    }

    /**
     * Processa blocos de código adicionando wrapper com header e botão de copiar
     */
    private processCodeBlocks(container: HTMLElement): void {
        const preBlocks = container.querySelectorAll('pre');
        preBlocks.forEach(pre => {
            if (pre.parentElement?.classList.contains('code-block-wrapper')) return;

            const code = pre.querySelector('code');
            let language = '';
            let displayLanguage = 'Código';
            let isTerminal = false;

            if (code) {
                const langClass = Array.from(code.classList).find(c => c.startsWith('language-'));
                if (langClass) {
                    language = langClass.replace('language-', '').toLowerCase();
                    displayLanguage = languageMap[language] || language.toUpperCase();
                    isTerminal = terminalLanguages.includes(language);
                }
            }

            const wrapper = document.createElement('div');
            wrapper.className = 'code-block-wrapper';
            if (isTerminal) wrapper.classList.add('is-terminal');

            const copyIcon = isTerminal
                ? `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="4 17 10 11 4 5"></polyline>
                        <line x1="12" y1="19" x2="20" y2="19"></line>
                    </svg>`
                : `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>`;

            const header = document.createElement('div');
            header.className = 'code-block-header';
            header.innerHTML = `
                <span class="code-lang">${displayLanguage}</span>
                <button type="button" class="code-copy-btn" title="Copiar ${isTerminal ? 'comando' : 'código'}">
                    ${copyIcon}
                    <span>Copiar</span>
                </button>
            `;

            pre.parentNode?.insertBefore(wrapper, pre);
            wrapper.appendChild(header);
            wrapper.appendChild(pre);
        });
    }

    setup(): void {
        this.body = this.$refs.body as HTMLDivElement;
        this.form = this.$refs.form as HTMLFormElement;
        this.input = this.$refs.input as HTMLTextAreaElement;
        this.messageTemplate = this.$refs.messageTemplate as HTMLTemplateElement;
        this.typingIndicatorTemplate = this.$refs.typingIndicatorTemplate as HTMLTemplateElement;
        this.historyLoadingTemplate = this.$refs.historyLoadingTemplate as HTMLTemplateElement;
        this.stopButton = this.$refs.stopButton as HTMLButtonElement;
        this.fileInput = this.$refs.fileInput as HTMLInputElement;
        this.attachBtn = this.$refs.attachBtn as HTMLButtonElement;
        this.previewContainer = this.$refs.preview as HTMLDivElement;
        this.footerContainer = this.$refs.footer as HTMLDivElement;
        this.sendButton = (this.$refs.sendButton as HTMLButtonElement) || this.form.querySelector('button[type="submit"]');
        this.initialLoading = this.$refs.initialLoading as HTMLDivElement;

        if (this.fileInput) {
            this.fileInput.setAttribute('accept', ChatFileUploadService.ALLOWED_EXTENSIONS.map(ext => '.' + ext).join(','));
        }

        if (!this.messageTemplate) throw new Error('Chat component: Templates not found.');

        if (window.currentUser) {
            this.userAvatar = window.currentUser.avatar_url;
            this.userName = window.currentUser.name;
        }

        this.userLoggedIn = this.$opts.userLoggedIn === 'true';
        this.loginUrl = this.$opts.loginUrl || '/login';
        this.sessionCookieName = this.$opts.sessionCookieName || 'athena_session_id';

        // Garante que o guest session id existe no localStorage desde o primeiro load.
        // Isso permite que o servidor identifique o guest mesmo que o cookie seja perdido.
        if (!this.userLoggedIn) {
            this.getOrCreateGuestSessionId();
        }

        if (!this.userLoggedIn && !this.loginPromptShown) {
            this.showLoginSuggestionModal();
            this.loginPromptShown = true;
        }

        if (this.listenersRegistered) {
            aiLogger.warn('[Athena Chat] setup() chamado novamente, ignorando registro duplicado de listeners');
            return;
        }
        this.listenersRegistered = true;

        this.thinkingManager = new AiThinkingManager({
            onBeforeRender: () => this.hideTypingIndicator(),
            onAfterRender: () => this.scrollToBottom(),
            onAskQuestionsPending: (payload) => {
                if (this.askQuestionsModalInstance) {
                    this.askQuestionsModalInstance.showQuestions({
                        requestId: payload.requestId!,
                        questions: payload.questions || [],
                        context: payload.context,
                    });
                }
            },
            onAskQuestionsTimeout: () => {
                this.askQuestionsModalInstance?.dismiss();
            }
        });

        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.input.addEventListener('input', this.handleInput.bind(this));
        this.input.addEventListener('keydown', this.handleKeydown.bind(this));
        this.input.addEventListener('paste', this.handlePaste.bind(this));
        this.body.addEventListener('click', this.handleBodyClick.bind(this));
        this.body.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        // Registra no document inteiro para interceptar antes que o browser abra o arquivo
        document.addEventListener('dragenter', this.handleDragEnter.bind(this));
        document.addEventListener('dragover', this.handleDragOver.bind(this));
        document.addEventListener('dragleave', this.handleDragLeave.bind(this));
        document.addEventListener('drop', this.handleDrop.bind(this));

        this.createDropOverlay();
        this.createJumpToBottomButton();

        // Usa getElementById em vez de $refs porque o botão está dentro do componente
        // chat-sidebar (componente aninhado) — o BookStack não resolve refs entre componentes,
        // causando $refs.loginBtnSidebar === null e o listener nunca ser registrado.
        const loginBtnSidebar = document.getElementById('login-in-chat') as HTMLButtonElement;
        if (loginBtnSidebar) {
            loginBtnSidebar.addEventListener('click', () => this.showLoginSuggestionModal());
        }

        if (this.attachBtn && this.fileInput) {
            this.attachBtn.addEventListener('click', () => this.fileInput.click());
            this.fileInput.addEventListener('change', this.handleFileSelect.bind(this));
        }
        if (this.stopButton) {
            this.stopButton.addEventListener('click', this.handleStopGeneration.bind(this));
            this.toggleStopButton(false);
        }

        this.initializeSidebar();

        this.loadPrompts();
        this.loadInitialHistory();

        // Instancia o modal de perguntas interativas diretamente (sem data-component no HTML)
        this.askQuestionsModalInstance = new AskQuestionsModal();
        this.askQuestionsModalInstance.setup();
    }

    /**
     * Carrega e exibe seletor de prompts disponíveis (estilo Gemini)
     */
    private async loadPrompts(): Promise<void> {
        try {
            const response = await fetch('/chat/prompts');
            const data = await response.json();

            if (data.prompts && data.prompts.length > 0) {
                this.prompts = data.prompts;
                const firstPrompt = this.prompts[0];
                this.currentPromptId = firstPrompt.id;
                this.currentPromptPlaceholder = firstPrompt.placeholder;

                this.createPromptDropdown();
                this.updateInputPlaceholder();
            }
        } catch (error) {
        }
    }

    /**
     * Cria o dropdown de prompts integrado na toolbar (estilo Gemini)
     */
    private createPromptDropdown(): void {
        const currentPrompt = this.prompts.find(p => p.id === this.currentPromptId) || this.prompts[0];

        this.promptButton = document.createElement('button');
        this.promptButton.type = 'button';
        this.promptButton.className = 'prompt-selector-pill';
        this.promptButton.title = 'Escolher assistente';
        this.promptButton.innerHTML = `
            <span class="prompt-pill-label">${currentPrompt.name}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        `;

        this.promptDropdown = document.createElement('div');
        this.promptDropdown.className = 'prompt-selector-menu';
        this.promptDropdown.style.display = 'none';

        this.prompts.forEach(prompt => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'prompt-selector-option';
            if (prompt.id === this.currentPromptId) {
                item.classList.add('selected');
            }

            item.innerHTML = `
                <span class="prompt-option-name">${prompt.name}</span>
                ${prompt.description ? `<span class="prompt-option-desc">${prompt.description}</span>` : ''}
            `;

            item.addEventListener('click', () => this.selectPrompt(prompt));
            this.promptDropdown.appendChild(item);
        });

        const toolbar = this.input.closest('.chat-input-group')?.querySelector('.chat-input-toolbar');

        if (toolbar) {
            const sendBtn = toolbar.querySelector('.chat-btn-send');
            if (sendBtn) {
                toolbar.insertBefore(this.promptButton, sendBtn);
            } else {
                toolbar.appendChild(this.promptButton);
            }
        } else {
            aiLogger.warn('[Athena] Toolbar não encontrada, tentando novamente em 100ms...');
            setTimeout(() => {
                const retryToolbar = this.input?.closest('.chat-input-group')?.querySelector('.chat-input-toolbar');
                if (retryToolbar) {
                    const sendBtn = retryToolbar.querySelector('.chat-btn-send');
                    if (sendBtn) {
                        retryToolbar.insertBefore(this.promptButton, sendBtn);
                    } else {
                        retryToolbar.appendChild(this.promptButton);
                    }
                    aiLogger.log('[Athena] Botão de prompt inserido após retry');
                } else {
                    aiLogger.error('[Athena] Toolbar não encontrada mesmo após retry. Prompts não disponíveis.');
                }
            }, 100);
        }

        document.body.appendChild(this.promptDropdown);

        this.promptButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.togglePromptDropdown();
        });

        document.addEventListener('click', (e) => {
            if (this.isPromptDropdownOpen &&
                !this.promptDropdown.contains(e.target as Node) &&
                !this.promptButton.contains(e.target as Node)) {
                this.closePromptDropdown();
            }
        });
    }

    /**
     * Alterna visibilidade do dropdown
     */
    private togglePromptDropdown(): void {
        this.isPromptDropdownOpen = !this.isPromptDropdownOpen;
        this.promptButton.classList.toggle('active', this.isPromptDropdownOpen);

        if (this.isPromptDropdownOpen) {
            const rect = this.promptButton.getBoundingClientRect();
            this.promptDropdown.style.display = 'block';
            const menuHeight = this.promptDropdown.offsetHeight;
            const menuWidth = this.promptDropdown.offsetWidth;

            let top = rect.top - menuHeight - 8;
            let left = rect.left + (rect.width / 2) - (menuWidth / 2);

            if (top < 8) top = rect.bottom + 8;
            if (left < 8) left = 8;
            if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;

            this.promptDropdown.style.top = `${top}px`;
            this.promptDropdown.style.left = `${left}px`;
        } else {
            this.promptDropdown.style.display = 'none';
        }
    }

    /**
     * Fecha o dropdown
     */
    private closePromptDropdown(): void {
        this.isPromptDropdownOpen = false;
        this.promptDropdown.style.display = 'none';
        this.promptButton.classList.remove('active');
    }

    /**
     * Seleciona um prompt
     */
    private selectPrompt(prompt: any): void {
        this.currentPromptId = prompt.id;
        this.currentPromptPlaceholder = prompt.placeholder;

        const promptLabel = this.promptButton.querySelector('.prompt-pill-label');
        if (promptLabel) {
            promptLabel.textContent = prompt.name;
        }

        this.promptDropdown.querySelectorAll('.prompt-selector-option').forEach(item => {
            item.classList.remove('selected');
        });

        const selectedItem = Array.from(this.promptDropdown.querySelectorAll('.prompt-selector-option'))
            .find(item => item.querySelector('.prompt-option-name')?.textContent === prompt.name);
        selectedItem?.classList.add('selected');

        this.updateInputPlaceholder();

        this.closePromptDropdown();
    }

    /**
     * Atualiza o placeholder do input
     */
    private updateInputPlaceholder(): void {
        if (this.input) {
            this.input.setAttribute('placeholder', this.currentPromptPlaceholder);
        }
    }

    private handleFileSelect(): void {
        if (!this.fileInput.files) return;
        const newFiles = Array.from(this.fileInput.files);
        this.selectedFiles.push(...newFiles);
        this.renderPreviews();
        this.fileInput.value = '';
    }

    private renderPreviews(): void {
        if (!this.previewContainer) return;
        this.previewContainer.innerHTML = '';
        if (this.selectedFiles.length === 0) {
            this.previewContainer.classList.add('hidden');
            this.footerContainer.classList.remove('file-active');
            return;
        }
        this.previewContainer.classList.remove('hidden');
        this.footerContainer.classList.add('file-active');
        this.selectedFiles.forEach((file, index) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'preview-item';
            if (file.type.startsWith('image/')) {
                // Spinner visível até a imagem carregar
                const spinner = document.createElement('div');
                spinner.className = 'preview-item-spinner';
                spinner.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.7s" repeatCount="indefinite"/></path></svg>`;
                wrapper.appendChild(spinner);

                const img = document.createElement('img');
                img.alt = file.name;
                img.style.opacity = '0';
                img.style.transition = 'opacity 0.2s ease';
                img.onload = () => {
                    spinner.remove();
                    img.style.opacity = '1';
                };
                img.src = URL.createObjectURL(file);
                wrapper.appendChild(img);
            } else {
                const fileDisplay = document.createElement('div');
                fileDisplay.className = 'file-display';
                const iconWrapper = document.createElement('div');
                iconWrapper.className = 'file-icon-wrapper';
                iconWrapper.innerHTML = getFileIcon(file.name);
                fileDisplay.appendChild(iconWrapper);
                const fileNameSpan = document.createElement('span');
                fileNameSpan.className = 'file-name';
                fileNameSpan.textContent = file.name.length > 15 ? `${file.name.substring(0, 12)}...` : file.name;
                fileDisplay.appendChild(fileNameSpan);
                wrapper.appendChild(fileDisplay);
            }
            const btn = document.createElement('button');
            btn.innerHTML = '×';
            btn.type = 'button';
            btn.onclick = e => { e.preventDefault(); this.removeFile(index); };
            wrapper.appendChild(btn);
            this.previewContainer.appendChild(wrapper);
        });
    }

    private removeFile(index: number): void {
        this.selectedFiles.splice(index, 1);
        this.renderPreviews();
    }

    /**
     * Bloqueia o botão enviar enquanto imagens remotas estão sendo buscadas via paste.
     */
    private showPasteLoadingSkeletons(): void {
        if (!this.previewContainer) return;
        this.isFilesLoading = true;
        if (this.sendButton) {
            this.sendButton.disabled = true;
            this.sendButton.classList.add('files-loading');
        }
        this.previewContainer.classList.remove('hidden');
        this.footerContainer.classList.add('file-active');
        const skeleton = document.createElement('div');
        skeleton.className = 'preview-item preview-item-loading';
        skeleton.dataset.pasteLoader = '1';
        skeleton.innerHTML = `<div class="preview-item-spinner"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.7s" repeatCount="indefinite"/></path></svg></div>`;
        this.previewContainer.appendChild(skeleton);
    }

    /**
     * Remove skeleton de paste e libera o botão enviar.
     */
    private clearPasteLoadingSkeletons(): void {
        if (!this.previewContainer) return;
        this.isFilesLoading = false;
        if (this.sendButton) {
            this.sendButton.disabled = false;
            this.sendButton.classList.remove('files-loading');
        }
        const loaders = this.previewContainer.querySelectorAll('[data-paste-loader]');
        loaders.forEach(el => el.remove());
        if (this.selectedFiles.length === 0 && this.previewContainer.children.length === 0) {
            this.previewContainer.classList.add('hidden');
            this.footerContainer.classList.remove('file-active');
        }
    }


    /**
     * Intercepta o evento paste no contenteditable.
     * 
     * Fluxo:
     * 1. Verifica se há imagens diretas no clipboard ou no HTML colado.
     * 2. Força a colagem do texto como Plain Text para evitar "letras gigantes" e formatações externas.
     */
    private async handlePaste(event: ClipboardEvent): Promise<void> {
        if (this.isStreaming) return;

        // 1. Tenta extrair arquivos (imagens do clipboard ou extraídas de HTML)
        const files = await ChatFileUploadService.getFilesFromPaste(event);
        if (files.length > 0) {
            event.preventDefault();
            this.addFilesToSelection(files);
        }

        const text = event.clipboardData?.getData('text/plain');
        if (text) {
            event.preventDefault();
            document.execCommand('insertText', false, text);
        }
    }




    /**
     * Cria o overlay visual de drop zone.
     * Elemento fixo no DOM, exibido/oculto via classe CSS.
     */
    private createDropOverlay(): void {
        this.dropOverlay = document.createElement('div');
        this.dropOverlay.className = 'chat-drop-overlay';
        this.dropOverlay.innerHTML = `
            <div class="chat-drop-overlay-content">
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span class="chat-drop-overlay-text">Solte os arquivos aqui</span>
                <span class="chat-drop-overlay-hint">Imagens, PDFs, documentos e mais</span>
            </div>
        `;

        document.documentElement.appendChild(this.dropOverlay);
    }

    /**
     * Mostra overlay de drop.
     * Usa dragEnterCounter para evitar flicker em Chrome/Firefox
     * (dragenter/dragleave disparam múltiplas vezes ao passar sobre filhos).
     */
    private showDropOverlay(): void {
        if (this.dropOverlay) {
            this.dropOverlay.style.top = '0';
            this.dropOverlay.style.left = '0';
            this.dropOverlay.style.width = '100vw';
            this.dropOverlay.style.height = '100vh';
            this.dropOverlay.classList.add('active');
        }
        this.form.classList.add('drag-over');
    }

    /**
     * Esconde overlay de drop.
     */
    private hideDropOverlay(): void {
        if (this.dropOverlay) {
            this.dropOverlay.classList.remove('active');
        }
        this.form.classList.remove('drag-over');
        this.dragEnterCounter = 0;
    }

    /**
     * Verifica se o drag contém arquivos (ignora drags de texto/links internos).
     */
    private dragHasFiles(event: DragEvent): boolean {
        if (!event.dataTransfer) return false;
        if (event.dataTransfer.types.indexOf('Files') !== -1) return true;
        if (event.dataTransfer.types.indexOf('application/x-moz-file') !== -1) return true;
        return false;
    }

    /**
     * dragenter: incrementa counter e mostra overlay.
     * Padrão cross-browser: cada enter em filho incrementa,
     * cada leave decrementa. Quando volta a 0 = saiu de verdade.
     */
    private handleDragEnter(event: DragEvent): void {
        event.preventDefault();
        if (!this.dragHasFiles(event)) return;

        this.dragEnterCounter++;
        if (this.dragEnterCounter === 1) {
            this.showDropOverlay();
        }
    }

    /**
     * dragover: necessário para permitir o drop (padrão do browser é negar).
     */
    private handleDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
    }

    /**
     * dragleave: decrementa counter. Quando chega a 0, esconde overlay.
     * Funciona corretamente em Chrome e Firefox sem relatedTarget.
     */
    private handleDragLeave(event: DragEvent): void {
        event.preventDefault();
        if (!this.dragHasFiles(event)) return;

        // relatedTarget === null = cursor saiu da janela do browser
        if (event.relatedTarget === null) {
            this.dragEnterCounter = 0;
            this.hideDropOverlay();
            return;
        }

        this.dragEnterCounter--;
        if (this.dragEnterCounter <= 0) {
            this.hideDropOverlay();
        }
    }

    /**
     * drop: processa os arquivos soltos e esconde overlay.
     * Suporta tanto dataTransfer.files quanto items (imagens do browser).
     */
    private handleDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.hideDropOverlay();

        const files = ChatFileUploadService.getFilesFromDrop(event);
        if (files.length > 0) {
            this.addFilesToSelection(files);
            aiLogger.log(`[Athena Chat] Drop: ${files.length} arquivo(s) recebido(s)`);
        }
    }


    /**
     * Adiciona arquivos ao selectedFiles[] com validação.
     * Método centralizado usado por: handleFileSelect, handlePaste, handleDrop.
     */
    private addFilesToSelection(newFiles: File[]): void {
        if (newFiles.length === 0) return;

        const validFiles = newFiles.filter(file => ChatFileUploadService.isValidFile(file));

        if (validFiles.length === 0) {
            window.$events.error('Nenhum arquivo válido encontrado.');
            return;
        }

        this.selectedFiles.push(...validFiles);

        this.renderPreviews();
    }

    private async loadInitialHistory(): Promise<void> {
        try {
            await this.fetchAndRenderHistory();
            this.scrollToBottom(true);
        } finally {
            this.hideInitialLoading();
            this.restoreDraft();
        }
    }

    private hideInitialLoading(): void {
        if (this.initialLoading) {
            this.initialLoading.style.opacity = '0';
            setTimeout(() => {
                this.initialLoading.style.display = 'none';
            }, 300);
        }
    }

    private async fetchAndRenderHistory(beforeId?: number): Promise<void> {
        if (this.isLoadingHistory) return;
        if (beforeId && !this.hasMoreHistory) return;
        this.isLoadingHistory = true;
        if (beforeId) this.showHistoryLoading();

        try {
            const data = await ChatConversationService.fetchHistory(this.currentConversationId, beforeId);

            if (data.history && data.history.length > 0) {
                this.removeWelcomeMessage();
                this.firstMessageId = data.history[0].id;
                this.hasMoreHistory = data.has_more;

                if (data.history[0].conversation_id && !this.currentConversationId) {
                    this.currentConversationId = data.history[0].conversation_id;
                }

                const oldScrollHeight = this.body.scrollHeight;
                if (beforeId) {
                    [...data.history].reverse().forEach((message: HistoryMessage) => {
                        this.addMessage('ai', message.response, true, message.id, message.is_liked, [], message.has_error, message.query, message.is_disliked || false);
                        this.addMessage('user', message.query, true, 0, false, message.attachments || []);
                    });
                    this.body.scrollTop = this.body.scrollHeight - oldScrollHeight;
                } else {
                    data.history.forEach((message: HistoryMessage) => {
                        this.addMessage('user', message.query, false, 0, false, message.attachments || []);
                        this.addMessage('ai', message.response, false, message.id, message.is_liked, [], message.has_error, message.query, message.is_disliked || false);
                    });
                    this.scrollToBottom(true);
                }
            } else {
                this.hasMoreHistory = false;
                if (!beforeId) this.showWelcomeMessage();
            }
        } catch (error: any) {
            aiLogger.error('Failed to fetch chat history:', error);
        } finally {
            this.isLoadingHistory = false;
            this.hideHistoryLoading();
        }
    }

    private handleScroll(): void {
        // "Grudado" apenas quando o usuário está (quase) no fim.
        const distanceFromBottom = this.body.scrollHeight - this.body.scrollTop - this.body.clientHeight;
        this.autoScrollPinned = distanceFromBottom < 60;
        this.jumpBottomBtn?.classList.toggle('hidden', this.autoScrollPinned);

        // Só carrega mais histórico se:
        // 1. O usuário de fato scrollou até o topo (scrollTop === 0)
        // 2. O container TEM scroll real (conteúdo maior que o viewport)
        // 3. Há mais histórico disponível no servidor
        // 4. firstMessageId está definido (evita carga inicial duplicada)
        // 5. Não está já carregando
        const isScrollable = this.body.scrollHeight > this.body.clientHeight;
        if (
            this.body.scrollTop === 0 &&
            isScrollable &&
            this.hasMoreHistory &&
            this.firstMessageId !== null &&
            !this.isLoadingHistory
        ) {
            this.fetchAndRenderHistory(this.firstMessageId);
        }
    }

    private async handleSubmit(event: Event): Promise<void> {
        event.preventDefault();

        if (this.isFilesLoading || this.isStreaming) return;

        const query = this.input.innerText.trim();
        if (!query && this.selectedFiles.length === 0) return;

        this.isStreaming = true;
        this.abortController = new AbortController();
        this.autoScrollPinned = true;
        this.clearDraft();
        this.removeWelcomeMessage();

        const tempAttachments = this.selectedFiles.map(file => ({
            name: file.name,
            url: URL.createObjectURL(file),
            type: file.type,
            id: 0,
            path: ''
        }));

        let displayQuery = query;
        if (!displayQuery && tempAttachments.length > 0) {
            const fileNames = tempAttachments.map(f => f.name);
            displayQuery = fileNames.length === 1
                ? `Enviou o arquivo: ${fileNames[0]}`
                : `Enviou ${fileNames.length} arquivos: ${fileNames.join(', ')}`;
        }
        const userMessageContainer = this.addMessage('user', displayQuery, false, 0, false, tempAttachments);

        this.input.innerHTML = '';
        this.handleInput();

        const filesToSend = [...this.selectedFiles];

        // Avisa a sidebar para renderizar itens otimistas imediatamente com dados do cache local.
        // Os File objects já têm name, size e type — dispensando skeletons.
        if (filesToSend.length > 0) {
            const optimisticFiles = filesToSend.map(file => ({
                name: file.name,
                size: file.size,
                mime_type: file.type || 'application/octet-stream',
            }));
            document.dispatchEvent(new CustomEvent('chat-files-uploading', {
                detail: { conversationId: this.currentConversationId, files: optimisticFiles }
            }));
        }

        this.selectedFiles = [];
        this.renderPreviews();
        this.toggleStopButton(true);
        this.showTypingIndicator();

        // Variáveis de estado do streaming — declaradas antes do try para ficarem
        // acessíveis tanto no bloco catch quanto no finally.
        let messageIdForLike = 0;
        let aiMessageContainer: HTMLDivElement | null = null;
        let aiMessageTextElement: HTMLDivElement | null = null;
        let responseStarted = false;
        let filesConfirmed = false;    // também usada no catch/finally

        this.thinkingManager.reset();

        try {
            const formData = new FormData();
            formData.append('query', query);
            formData.append('prompt_id', this.currentPromptId);
            if (this.currentConversationId) {
                formData.append('conversation_id', this.currentConversationId.toString());
            }
            filesToSend.forEach(file => formData.append('files[]', file));

            const headers: Record<string, string> = {
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': getCsrfToken(),
            };

            // Sempre envia o guest session ID como header de fallback.
            // O servidor usa quando o cookie não está disponível (HTTP dev, cookie perdido).
            if (!this.userLoggedIn) {
                headers['X-Chat-Session-Id'] = this.getOrCreateGuestSessionId();
            }

            const response = await fetch('/chat/message', {
                method: 'POST',
                headers,
                body: formData,
                signal: this.abortController.signal
            });

            if (response.status === 419) {
                this.hideTypingIndicator();
                this.showSessionExpiredMessage();
                return;
            }

            if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

            const userMessageId = response.headers.get('X-User-Message-ID');
            const conversationId = response.headers.get('X-Conversation-ID');
            const conversationTitle = response.headers.get('X-Conversation-Title');
            const attachmentsHeader = response.headers.get('X-Attachments');
            let serverAttachments: Attachment[] = [];
            filesConfirmed = false; // reinicia para este streaming

            if (conversationId && !this.currentConversationId) {
                this.currentConversationId = parseInt(conversationId, 10);
                // Inclui o título no evento para que a sidebar possa criar o card
                // sem precisar recarregar a API (necessário para usuários guest).
                document.dispatchEvent(new CustomEvent('chat-conversation-created', {
                    detail: {
                        conversationId: this.currentConversationId,
                        title: conversationTitle || 'Nova Conversa',
                    }
                }));
            }

            // Os arquivos já estão salvos no storage assim que o servidor responde.
            // Dispara chat-files-uploaded com dados reais do header (URLs de download).
            // A sidebar substitui os itens otimistas pelos definitivos.
            if (filesToSend.length > 0 && this.currentConversationId) {
                if (attachmentsHeader) {
                    // Header trouxe dados — manda os arquivos reais para a sidebar
                    try {
                        const parsedAtt = JSON.parse(attachmentsHeader);
                        const msgId = userMessageId ? parseInt(userMessageId, 10) : 0;
                        const confirmedFiles = parsedAtt.map((att: any, index: number) => ({
                            message_id: msgId,
                            index: index,
                            name: att.file_name || att.name || 'arquivo',
                            mime_type: att.mime_type || att.type || 'application/octet-stream',
                            size: att.file_size || att.size || 0,
                            created_at: new Date().toISOString(),
                            url: msgId ? `/chat/attachments/${msgId}/download/${index}` : '',
                        }));
                        document.dispatchEvent(new CustomEvent('chat-files-uploaded', {
                            detail: { conversationId: this.currentConversationId, files: confirmedFiles }
                        }));
                        filesConfirmed = true;
                    } catch (e) {
                        // Fallback: recarrega a lista completa
                        document.dispatchEvent(new CustomEvent('chat-files-uploaded', {
                            detail: { conversationId: this.currentConversationId }
                        }));
                        filesConfirmed = true;
                    }
                } else {
                    // Sem header — faz reload completo da sidebar
                    document.dispatchEvent(new CustomEvent('chat-files-uploaded', {
                        detail: { conversationId: this.currentConversationId }
                    }));
                    filesConfirmed = true;
                }
            }

            if (conversationTitle && conversationId) {
                document.dispatchEvent(new CustomEvent('chat-conversation-title-updated', {
                    detail: {
                        conversationId: parseInt(conversationId, 10),
                        title: conversationTitle
                    }
                }));
            }

            if (attachmentsHeader) {
                try {
                    const parsedAttachments = JSON.parse(attachmentsHeader);
                    serverAttachments = parsedAttachments.map((att: any, index: number) => ({
                        id: 0,
                        name: att.file_name || att.name || 'arquivo',
                        path: att.path || '',
                        type: att.mime_type || att.type || '',
                        url: userMessageId ? `/chat/attachments/${userMessageId}/download/${index}` : '',
                        messageId: userMessageId ? parseInt(userMessageId, 10) : 0,
                        index: index
                    }));
                } catch (e) {
                    aiLogger.warn('Falha ao parsear X-Attachments header:', e);
                }
            }

            if (userMessageContainer) {
                const likeBtn = userMessageContainer.querySelector('.chat-message-like') as HTMLButtonElement;
                if (likeBtn && userMessageId) {
                    likeBtn.dataset.id = userMessageId;
                }
                const dislikeBtn = userMessageContainer.querySelector('.chat-message-dislike') as HTMLButtonElement;
                if (dislikeBtn && userMessageId) {
                    dislikeBtn.dataset.id = userMessageId;
                }

                if (serverAttachments.length > 0) {
                    const updatedAttachmentsEl = userMessageContainer.querySelector('.chat-message-attachment-previews') as HTMLDivElement;
                    if (updatedAttachmentsEl) {
                        updatedAttachmentsEl.innerHTML = '';
                        updatedAttachmentsEl.classList.remove('hidden');
                        serverAttachments.forEach((file: Attachment) => {
                            updatedAttachmentsEl.appendChild(this.createAttachmentElement(file));
                        });
                    }
                }
            }

            messageIdForLike = userMessageId ? parseInt(userMessageId, 10) : 0;

            // --- STREAM HANDLING ---
            let aiMessageContainer: HTMLDivElement | null = null;
            let aiMessageTextElement: HTMLDivElement | null = null;
            let renderScheduled = false;
            let pendingRafId: number | null = null;
            let lastContentToRender = '';

            const scheduleRender = (content: string): void => {
                lastContentToRender = content;
                if (renderScheduled) return;

                renderScheduled = true;
                pendingRafId = requestAnimationFrame(() => {
                    pendingRafId = null;
                    renderScheduled = false;
                    if (aiMessageTextElement) {
                        aiMessageTextElement.innerHTML = this.md.render(lastContentToRender);
                    }
                    // Só acompanha o stream se o usuário estiver no fim da conversa
                    if (this.autoScrollPinned) {
                        this.body.scrollTop = this.body.scrollHeight;
                    }
                });
            };

            const ensureAiMessage = () => {
                if (!aiMessageContainer) {
                    this.hideTypingIndicator();
                    aiMessageContainer = this.addMessage('ai', '', false, messageIdForLike);
                    aiMessageTextElement = aiMessageContainer.querySelector('.chat-message-text') as HTMLDivElement;
                }
            };

            const fullResponse = await readSSEStream(response, {
                onToolProgress: (data) => {
                    ensureAiMessage();
                    this.thinkingManager.handleProgress(
                        data,
                        aiMessageTextElement!.parentElement!,
                        aiMessageTextElement!
                    );
                },
                onSearchStatus: (status) => {
                    ensureAiMessage();
                    this.thinkingManager.handleSearchStatus(
                        status,
                        aiMessageTextElement!.parentElement!,
                        aiMessageTextElement!
                    );
                },
                onContentStart: () => {
                    aiLogger.log('[AiChat] [CONTENT_START] recebido — Iniciando renderização do conteúdo');
                    ensureAiMessage();
                    this.thinkingManager.collapse();
                },
                onContentChunk: (content) => {
                    scheduleRender(content);
                },
                onAthenaError: (errorMsg) => {
                    ensureAiMessage();
                    this.showErrorMessage(aiMessageContainer!, aiMessageTextElement!, errorMsg || 'Erro interno no stream.', query, filesToSend);
                }
            });

            // --- STREAM FINALIZADO ---
            if (aiMessageTextElement) {
                if (pendingRafId !== null) {
                    cancelAnimationFrame(pendingRafId);
                }
                if (fullResponse) {
                    aiMessageTextElement.innerHTML = this.md.render(fullResponse);
                } else {
                    aiLogger.warn('[AiChat] Stream retornou conteúdo vazio no final, mantendo renderização incremental.');
                }
                this.thinkingManager.collapse(true);
                this.processCodeBlocks(aiMessageTextElement);
                ArtifactRenderer.processArtifacts(aiMessageTextElement);
            }
        } catch (error: any) {
            this.hideTypingIndicator();
            if (error.name !== 'AbortError') {
                const errorMsgContainer = this.addMessage('ai', '', false);
                const errorMsgText = errorMsgContainer.querySelector('.chat-message-text') as HTMLDivElement;
                this.showErrorMessage(errorMsgContainer, errorMsgText, 'Ops, tive algum problema para te responder. Tente mais tarde.', query, filesToSend);
            } else {
                this.thinkingManager.reset();

                // Quando o abort foi causado por troca de conversa (não pelo botão Stop),
                // mostra um badge no card da sidebar da conversa anterior e não tenta
                // escrever no DOM da conversa atual (que já foi substituído).
                if (this.abortedBySwitch) {
                    if (this.prevStreamingConversationId !== null) {
                        this.sidebar?.markConversationInterrupted(this.prevStreamingConversationId);
                    }
                    this.abortedBySwitch = false;
                    this.prevStreamingConversationId = null;
                } else {
                    // Abort normal (botão Stop): marca a resposta como interrompida no DOM atual.
                    const interruptedText = '_Resposta interrompida pelo usuário._';
                    if (aiMessageContainer && aiMessageTextElement) {
                        aiMessageTextElement.innerHTML = this.md.render(interruptedText);
                    } else if (aiMessageContainer) {
                        const textEl = aiMessageContainer.querySelector('.chat-message-text') as HTMLDivElement;
                        if (textEl) textEl.innerHTML = this.md.render(interruptedText);
                    } else {
                        this.addMessage('ai', interruptedText, false);
                    }
                }

                if (messageIdForLike) {
                    ChatConversationService.markInterrupted(messageIdForLike, getCsrfToken());
                }
            }
            if (filesToSend.length > 0 && !filesConfirmed) {
                document.dispatchEvent(new CustomEvent('chat-files-upload-failed', {
                    detail: { conversationId: this.currentConversationId }
                }));
            }
        } finally {
            this.isStreaming = false;
            this.abortController = null;
            this.toggleStopButton(false);
            this.hideTypingIndicator();
            this.scrollToBottom();
            tempAttachments.forEach(att => URL.revokeObjectURL(att.url));

            // A sidebar recarrega o menu de alterações ao receber este evento.
            document.dispatchEvent(new CustomEvent('chat-stream-finished', {
                detail: { conversationId: this.currentConversationId }
            }));

            this.askQuestionsModalInstance?.dismiss();
            if (filesToSend.length > 0 && this.currentConversationId && !filesConfirmed) {
                document.dispatchEvent(new CustomEvent('chat-files-uploaded', {
                    detail: { conversationId: this.currentConversationId }
                }));
            }
        }
    }

    /**
     * Exibe mensagem de sessao expirada com opcao de recarregar a pagina
     */
    private showSessionExpiredMessage(): void {
        const messageContainer = this.addMessage('ai', '', false);
        const textEl = messageContainer.querySelector('.chat-message-text') as HTMLDivElement;

        if (textEl) {
            textEl.innerHTML = `
                <div class="session-expired-message" style="text-align: center; padding: 16px;">
                    <div style="font-size: 2em; margin-bottom: 12px;">⚠️</div>
                    <p style="margin-bottom: 8px;"><strong>Sua sessao expirou</strong></p>
                    <p style="color: var(--color-text-muted); margin-bottom: 16px;">
                        Por motivos de seguranca, sua sessao foi encerrada.<br>
                        Recarregue a pagina para continuar conversando.
                    </p>
                    <button type="button" class="button primary session-expired-reload-btn">
                        Recarregar Pagina
                    </button>
                </div>
            `;

            const reloadBtn = textEl.querySelector('.session-expired-reload-btn') as HTMLButtonElement;
            if (reloadBtn) {
                reloadBtn.addEventListener('click', () => {
                    window.location.reload();
                });
            }
        }

        this.scrollToBottom();
    }

    /**
     * Exibe mensagem de erro na bolha da IA com botão de reenviar
     */
    private showErrorMessage(
        container: HTMLDivElement,
        textEl: HTMLDivElement,
        errorMsg: string,
        originalQuery: string,
        originalFiles: File[]
    ): void {
        container.classList.add('chat-message-error');
        textEl.innerHTML = `
            <div class="chat-error-content">
                <span class="chat-error-icon">⚠️</span>
                <span class="chat-error-text">${errorMsg}</span>
            </div>
        `;

        const metaBar = container.querySelector('.chat-message-meta');
        if (metaBar) {
            const retryBtn = document.createElement('button');
            retryBtn.type = 'button';
            retryBtn.className = 'chat-message-retry';
            retryBtn.title = 'Tentar novamente';
            retryBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>`;

            retryBtn.addEventListener('click', () => {
                const messageContainer = container.closest('.chat-message-container');
                if (messageContainer) messageContainer.remove();

                this.retryMessage(originalQuery, originalFiles);
            });

            const copyBtn = metaBar.querySelector('.chat-message-copy');
            if (copyBtn) {
                metaBar.insertBefore(retryBtn, copyBtn);
            } else {
                metaBar.appendChild(retryBtn);
            }
        }

        this.scrollToBottom();
    }

    /**
     * Reenvia uma mensagem que falhou
     */
    private async retryMessage(query: string, files: File[]): Promise<void> {
        this.input.innerText = query;
        this.selectedFiles = files;
        this.renderPreviews();

        const fakeEvent = new Event('submit', { cancelable: true });
        await this.handleSubmit(fakeEvent);
    }

    /**
     * Copia texto para o clipboard com fallback para HTTP
     */
    private copyToClipboard(text: string): Promise<void> {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        }

        return new Promise((resolve, reject) => {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            textarea.style.top = '-9999px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            try {
                const success = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (success) {
                    resolve();
                } else {
                    reject(new Error('execCommand copy failed'));
                }
            } catch (err) {
                document.body.removeChild(textarea);
                reject(err);
            }
        });
    }

    /**
     * Exibe modal sugerindo login para salvar historico de mensagens
     * Inclui formulario de login inline
     */
    private showLoginSuggestionModal(): void {
        const csrfToken = getCsrfToken();

        const modal = document.createElement('div');
        modal.className = 'login-suggestion-modal';
        modal.innerHTML = `
            <div class="login-modal-overlay"></div>
            <div class="login-modal-content">
                <button type="button" class="login-modal-close" data-action="close" title="Fechar">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
                
                <!-- Tela inicial de sugestao -->
                <div class="login-modal-step" data-step="suggestion">
                    <div class="login-modal-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                    </div>
                    <h3 class="login-modal-title">Salvar seu historico</h3>
                    <p class="login-modal-text">
                        Faca login para salvar suas conversas e acessar seu historico de mensagens em qualquer dispositivo.
                    </p>
                    <div class="login-modal-actions">
                        <button type="button" class="login-modal-btn-primary" data-action="show-login">
                            Fazer Login
                        </button>
                        <button type="button" class="login-modal-btn-secondary" data-action="continue">
                            Continuar sem login
                        </button>
                    </div>
                </div>
                
                <!-- Formulario de login -->
                <div class="login-modal-step hidden" data-step="form">
                    <h3 class="login-modal-title">Entrar na sua conta</h3>
                    <form class="login-modal-form" data-login-form>
                        <input type="hidden" name="_token" value="${csrfToken}">
                        
                        <div class="login-form-group">
                            <label for="login-email">E-mail</label>
                            <input type="email" id="login-email" name="email" required placeholder="seu@email.com" autocomplete="email">
                        </div>
                        
                        <div class="login-form-group">
                            <label for="login-password">Senha</label>
                            <input type="password" id="login-password" name="password" required placeholder="Sua senha" autocomplete="current-password">
                        </div>
                        
                        <div class="login-form-remember">
                            <label class="login-checkbox">
                                <input type="checkbox" name="remember" value="on">
                                <span>Lembrar de mim</span>
                            </label>
                        </div>
                        
                        <div class="login-form-error hidden" data-login-error></div>
                        
                        <div class="login-modal-actions">
                            <button type="submit" class="login-modal-btn-primary">
                                <span class="btn-text">Entrar</span>
                                <span class="btn-loading hidden">Entrando...</span>
                            </button>
                            <button type="button" class="login-modal-btn-secondary" data-action="back">
                                Voltar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const suggestionStep = modal.querySelector('[data-step="suggestion"]') as HTMLDivElement;
        const formStep = modal.querySelector('[data-step="form"]') as HTMLDivElement;
        const loginForm = modal.querySelector('[data-login-form]') as HTMLFormElement;
        const errorDiv = modal.querySelector('[data-login-error]') as HTMLDivElement;
        const submitBtn = loginForm?.querySelector('button[type="submit"]') as HTMLButtonElement;
        const btnText = submitBtn?.querySelector('.btn-text') as HTMLSpanElement;
        const btnLoading = submitBtn?.querySelector('.btn-loading') as HTMLSpanElement;

        const closeModal = () => {
            modal.remove();
        };

        const showStep = (step: 'suggestion' | 'form') => {
            suggestionStep.classList.toggle('hidden', step !== 'suggestion');
            formStep.classList.toggle('hidden', step !== 'form');
            if (step === 'form') {
                (modal.querySelector('#login-email') as HTMLInputElement)?.focus();
            }
        };

        modal.querySelector('[data-action="close"]')?.addEventListener('click', closeModal);
        modal.querySelector('[data-action="continue"]')?.addEventListener('click', closeModal);
        modal.querySelector('.login-modal-overlay')?.addEventListener('click', closeModal);
        modal.querySelector('[data-action="show-login"]')?.addEventListener('click', () => showStep('form'));
        modal.querySelector('[data-action="back"]')?.addEventListener('click', () => showStep('suggestion'));

        loginForm?.addEventListener('submit', async (e) => {
            e.preventDefault();

            const formData = new FormData(loginForm);
            let sessionId: string | null = null;
            for (const storage of [localStorage, sessionStorage]) {
                try {
                    const val = storage.getItem(AiChat.GUEST_SESSION_KEY);
                    if (val) { sessionId = val; break; }
                } catch { /* storage bloqueado — tenta próximo */ }
            }

            submitBtn.disabled = true;
            btnText?.classList.add('hidden');
            btnLoading?.classList.remove('hidden');
            errorDiv?.classList.add('hidden');

            try {
                const response = await fetch('/login', {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    body: formData,
                    credentials: 'same-origin',
                    redirect: 'manual' // Captura redirect ao inves de seguir
                });

                const loginSuccess = response.type === 'opaqueredirect' || response.ok || response.status === 302;

                aiLogger.log('Login response:', {
                    type: response.type,
                    status: response.status,
                    ok: response.ok,
                    redirected: response.redirected,
                    loginSuccess
                });

                if (loginSuccess) {
                    btnLoading.textContent = 'Sincronizando...';

                    if (sessionId) {
                        try {
                            const pageResponse = await fetch(window.location.href, { credentials: 'same-origin' });
                            const pageHtml = await pageResponse.text();
                            const newTokenMatch = pageHtml.match(/meta name="csrf-token" content="([^"]+)"/);
                            const newCsrfToken = newTokenMatch ? newTokenMatch[1] : csrfToken;

                            const migratedCount = await ChatConversationService.migrateGuestSession(newCsrfToken);
                            if (migratedCount > 0) {
                                aiLogger.log(`[AiChat] Migradas ${migratedCount} mensagens para sua conta`);
                            }
                        } catch (migrateErr) {
                            aiLogger.warn('[AiChat] Não foi possível migrar mensagens:', migrateErr);
                        }
                    } else {
                        aiLogger.log('Nenhum session_id para migrar');
                    }

                    window.location.reload();
                } else {
                    let errorMessage = 'Credenciais invalidas. Verifique seu e-mail e senha.';
                    try {
                        const data = await response.json();
                        if (data.message) errorMessage = data.message;
                        if (data.errors?.email) errorMessage = data.errors.email[0];
                    } catch {
                    }

                    errorDiv.textContent = errorMessage;
                    errorDiv.classList.remove('hidden');
                }
            } catch (err) {
                errorDiv.textContent = 'Erro de conexao. Tente novamente.';
                errorDiv.classList.remove('hidden');
            } finally {
                submitBtn.disabled = false;
                btnText?.classList.remove('hidden');
                btnLoading?.classList.add('hidden');
            }
        });

        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEsc);
            }
        };
        document.addEventListener('keydown', handleEsc);
    }


    private addMessage(
        sender: 'user' | 'ai',
        content: string,
        prepend: boolean = false,
        messageId: number = 0,
        isLiked: boolean = false,
        attachments: Attachment[] = [],
        hasError: boolean = false,
        originalQuery: string = '',
        isDisliked: boolean = false
    ): HTMLDivElement {
        const clone = this.messageTemplate.content.cloneNode(true) as DocumentFragment;
        const container = clone.querySelector('.chat-message') as HTMLDivElement;
        const avatar = clone.querySelector('.chat-message-avatar img') as HTMLImageElement;
        const name = clone.querySelector('.chat-message-name') as HTMLSpanElement;
        const textEl = clone.querySelector('.chat-message-text') as HTMLDivElement;
        const attachmentsEl = clone.querySelector('.chat-message-attachment-previews') as HTMLDivElement;
        const likeBtn = clone.querySelector('.chat-message-like') as HTMLButtonElement;
        const dislikeBtn = clone.querySelector('.chat-message-dislike') as HTMLButtonElement;

        container.classList.add(sender);
        avatar.src = sender === 'user' ? this.userAvatar : this.aiAvatar;
        name.textContent = sender === 'user' ? this.userName : this.aiName;
        if (content) {
            textEl.innerHTML = this.md.render(content);
            this.processCodeBlocks(textEl);
            ArtifactRenderer.processArtifacts(textEl);
        }

        attachmentsEl.classList.add('hidden');

        if (Array.isArray(attachments) && attachments.length > 0 && attachmentsEl) {
            attachmentsEl.classList.remove('hidden');
            attachmentsEl.innerHTML = '';
            const displayLimit = 4;

            const visibleItems = attachments.slice(0, displayLimit);
            const hiddenItems = attachments.slice(displayLimit);

            visibleItems.forEach(file => {
                attachmentsEl.appendChild(this.createAttachmentElement(file));
            });

            if (hiddenItems.length > 0) {
                const moreButton = document.createElement('button');
                moreButton.className = 'attachment-more-btn';
                moreButton.textContent = `+${hiddenItems.length}`;
                moreButton.title = 'Mostrar mais anexos';
                attachmentsEl.appendChild(moreButton);

                const dropdown = document.createElement('div');
                dropdown.className = 'attachment-dropdown';
                hiddenItems.forEach(file => {
                    dropdown.appendChild(this.createAttachmentElement(file));
                });
                attachmentsEl.appendChild(dropdown);

                const hideDropdown = () => {
                    dropdown.classList.remove('visible');
                    document.removeEventListener('click', hideDropdown);
                };

                moreButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isVisible = dropdown.classList.toggle('visible');
                    if (isVisible) {
                        document.addEventListener('click', hideDropdown);
                    }
                });

                dropdown.addEventListener('click', e => e.stopPropagation());
            }
        }

        if (sender === 'ai' && likeBtn) {
            likeBtn.classList.remove('hidden');
            likeBtn.dataset.id = messageId.toString();
            if (isLiked) likeBtn.classList.add('text-primary');
        }

        if (sender === 'ai' && dislikeBtn) {
            dislikeBtn.classList.remove('hidden');
            dislikeBtn.dataset.id = messageId.toString();
            if (isDisliked) dislikeBtn.classList.add('is-active');
        }

        if (sender === 'ai' && hasError) {
            container.classList.add('chat-message-error');
            const errorText = content || 'Ops, tive algum problema para te responder. Tente mais tarde.';
            textEl.innerHTML = `
                <div class="chat-error-content">
                    <span class="chat-error-icon">⚠️</span>
                    <span class="chat-error-text">${errorText}</span>
                </div>
            `;

            const metaBar = container.querySelector('.chat-message-meta');
            if (metaBar) {
                const retryBtn = document.createElement('button');
                retryBtn.type = 'button';
                retryBtn.className = 'chat-message-retry';
                retryBtn.title = 'Tentar novamente';
                retryBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>`;

                retryBtn.addEventListener('click', () => {
                    const messageContainer = container.closest('.chat-message-container');
                    if (messageContainer) messageContainer.remove();
                    const queryToRetry = originalQuery || '';
                    if (queryToRetry) {
                        this.retryMessage(queryToRetry, []);
                    }
                });

                const copyBtn = metaBar.querySelector('.chat-message-copy');
                if (copyBtn) {
                    metaBar.insertBefore(retryBtn, copyBtn);
                } else {
                    metaBar.appendChild(retryBtn);
                }
            }
        }

        if (prepend) {
            const loader = this.body.querySelector('.history-loading-container');
            if (loader) this.body.insertBefore(clone, loader.nextSibling);
            else this.body.prepend(clone);
        } else {
            this.body.appendChild(clone);
            this.scrollToBottom();
        }

        return container;
    }

    private createAttachmentElement(file: Attachment): HTMLElement {
        const link = document.createElement('a');
        link.className = 'attachment-preview-item';
        link.title = `${file.name} - Clique para visualizar`;

        link.href = '#';
        link.addEventListener('click', (e) => {
            e.preventDefault();
            if (file.url) {
                this.openFileModal(file);
            } else {
                window.$events.error('URL de visualização não disponível.');
            }
        });

        if (file.type && file.type.startsWith('image/')) {
            const img = document.createElement('img');
            img.src = file.url;
            img.alt = file.name;
            link.appendChild(img);
        } else {
            const iconDiv = document.createElement('div');
            iconDiv.className = 'attachment-file-icon';
            iconDiv.innerHTML = getFileIcon(file.name);
            link.appendChild(iconDiv);

            const fileNameLabel = document.createElement('span');
            fileNameLabel.className = 'attachment-file-name';
            fileNameLabel.textContent = file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name;
            link.appendChild(fileNameLabel);
        }
        return link;
    }

    private openFileModal(file: Attachment): void {
        // Reutiliza o modal unificado file-preview-modal (mesmo da sidebar de arquivos)
        const modal = document.getElementById('file-preview-modal') as HTMLDivElement;
        if (!modal) return;

        const titleEl = document.getElementById('file-preview-title') as HTMLSpanElement;
        const downloadEl = document.getElementById('file-preview-download') as HTMLAnchorElement;
        const bodyEl = document.getElementById('file-preview-body') as HTMLDivElement;
        const closeEl = document.getElementById('file-preview-close') as HTMLButtonElement;
        const backdropEl = document.getElementById('file-preview-backdrop') as HTMLDivElement;

        // Título e download
        if (titleEl) titleEl.textContent = file.name;
        if (downloadEl) {
            downloadEl.href = file.url;
            downloadEl.download = file.name;
        }

        // Corpo do preview
        if (bodyEl) {
            bodyEl.innerHTML = '';

            const isImage = file.type && file.type.startsWith('image/');
            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            const isText = file.type && (
                file.type.startsWith('text/') ||
                file.type === 'application/json' ||
                ['txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'php', 'py'].some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
            );

            if (isImage) {
                const loader = document.createElement('div');
                loader.className = 'file-preview-loader';
                loader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                bodyEl.appendChild(loader);

                const img = document.createElement('img');
                img.alt = file.name;
                img.style.display = 'none';
                img.onload = () => { loader.remove(); img.style.display = ''; };
                img.onerror = () => { loader.innerHTML = '<span style="color:var(--color-text-muted)">Erro ao carregar a imagem.</span>'; };
                bodyEl.appendChild(img);
                img.src = file.url;

            } else if (isPdf) {
                const loader = document.createElement('div');
                loader.className = 'file-preview-loader';
                loader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                bodyEl.appendChild(loader);

                const iframe = document.createElement('iframe');
                iframe.title = file.name;
                iframe.style.display = 'none';
                iframe.onload = () => { loader.remove(); iframe.style.display = ''; };
                bodyEl.appendChild(iframe);
                iframe.src = file.url;

            } else if (isText) {
                const textLoader = document.createElement('div');
                textLoader.className = 'file-preview-loader';
                textLoader.innerHTML = '<div class="preview-spinner"></div><span>Carregando...</span>';
                bodyEl.appendChild(textLoader);

                const pre = document.createElement('pre');
                pre.style.display = 'none';
                bodyEl.appendChild(pre);

                fetch(file.url, { credentials: 'same-origin' })
                    .then(r => r.text())
                    .then(text => { textLoader.remove(); pre.textContent = text; pre.style.display = ''; })
                    .catch(() => { textLoader.remove(); pre.textContent = '(Erro ao carregar o conteúdo do arquivo)'; pre.style.display = ''; });

            } else {
                const unsupported = document.createElement('div');
                unsupported.className = 'file-preview-unsupported';
                unsupported.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                    <p>Pré-visualização não disponível para este tipo de arquivo.</p>
                `;
                bodyEl.appendChild(unsupported);
            }
        }

        // Abrir modal
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';

        // Fechar via botões (listeners one-shot)
        const closeModal = () => {
            modal.classList.add('hidden');
            document.body.style.overflow = '';
            if (bodyEl) bodyEl.innerHTML = '';
            closeEl?.removeEventListener('click', closeModal);
            backdropEl?.removeEventListener('click', closeModal);
            document.removeEventListener('keydown', handleEsc);
        };
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };

        closeEl?.addEventListener('click', closeModal);
        backdropEl?.addEventListener('click', closeModal);
        document.addEventListener('keydown', handleEsc);
    }

    private handleBodyClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const docModalBtn = target.closest('.chat-doc-modal-btn') as HTMLAnchorElement | HTMLButtonElement;
        if (docModalBtn) {
            event.preventDefault();
            const url = docModalBtn.dataset.url || docModalBtn.getAttribute('href');
            if (url) this.openDocModal(url);
            return;
        }

        const codeCopyBtn = target.closest('.code-copy-btn');
        if (codeCopyBtn) {
            const wrapper = codeCopyBtn.closest('.code-block-wrapper');
            const code = wrapper?.querySelector('pre code')?.textContent || wrapper?.querySelector('pre')?.textContent;
            if (code) {
                this.copyToClipboard(code).then(() => {
                    const btn = codeCopyBtn as HTMLButtonElement;
                    const span = btn.querySelector('span');
                    const originalText = span?.textContent || 'Copiar';
                    btn.classList.add('copied');
                    if (span) span.textContent = 'Copiado!';
                    setTimeout(() => {
                        btn.classList.remove('copied');
                        if (span) span.textContent = originalText;
                    }, 2000);
                }).catch(() => window.$events.error('Falha ao copiar.'));
            }
            return;
        }

        const copyBtn = target.closest('.chat-message-copy');
        if (copyBtn) {
            const text = copyBtn.closest('.chat-message-content')?.querySelector('.chat-message-text')?.textContent;
            if (text) {
                this.copyToClipboard(text)
                    .then(() => window.$events.success('Copiado!'))
                    .catch(() => window.$events.error('Falha ao copiar.'));
            }
            return;
        }
        const likeBtn = target.closest('.chat-message-like');
        if (likeBtn) {
            this.toggleLike(likeBtn as HTMLElement);
            return;
        }

        const dislikeBtn = target.closest('.chat-message-dislike');
        if (dislikeBtn) this.toggleDislike(dislikeBtn as HTMLElement);
    }

    // Retorna o status devolvido pelo servidor ('liked' | 'disliked' | 'cleared').
    private async sendFeedback(id: string, action: 'like' | 'dislike' | 'clear', comment?: string): Promise<string> {
        const response = await window.$http.post(`/chat/message/${id}/feedback`, {
            action,
            comment: comment || null,
        });
        return response.data?.status || '';
    }

    private applyFeedbackState(metaBar: Element | null, status: string): void {
        if (!metaBar) return;
        const likeBtn = metaBar.querySelector('.chat-message-like');
        const dislikeBtn = metaBar.querySelector('.chat-message-dislike');
        likeBtn?.classList.toggle('text-primary', status === 'liked');
        dislikeBtn?.classList.toggle('is-active', status === 'disliked');
    }

    private async toggleLike(btn: HTMLElement): Promise<void> {
        const id = btn.dataset.id;
        if (!id || id === '0') return;

        const metaBar = btn.closest('.chat-message-meta');
        const wasLiked = btn.classList.contains('text-primary');
        this.applyFeedbackState(metaBar, wasLiked ? '' : 'liked'); // Feedback visual imediato

        try {
            const status = await this.sendFeedback(id, wasLiked ? 'clear' : 'like');
            this.applyFeedbackState(metaBar, status);
        } catch (error) {
            this.applyFeedbackState(metaBar, wasLiked ? 'liked' : '');
            window.$events.error('Erro ao enviar feedback.');
        }
    }

    private async toggleDislike(btn: HTMLElement): Promise<void> {
        const id = btn.dataset.id;
        if (!id || id === '0') return;

        const metaBar = btn.closest('.chat-message-meta');
        const wasDisliked = btn.classList.contains('is-active');
        this.applyFeedbackState(metaBar, wasDisliked ? '' : 'disliked');

        try {
            const status = await this.sendFeedback(id, wasDisliked ? 'clear' : 'dislike');
            this.applyFeedbackState(metaBar, status);

            if (status === 'disliked') {
                this.showFeedbackCommentBox(btn, id);
            } else {
                this.removeFeedbackCommentBox(btn);
            }
        } catch (error) {
            this.applyFeedbackState(metaBar, wasDisliked ? 'disliked' : '');
            window.$events.error('Erro ao enviar feedback.');
        }
    }

    private showFeedbackCommentBox(dislikeBtn: HTMLElement, messageId: string): void {
        const contentEl = dislikeBtn.closest('.chat-message-content');
        if (!contentEl || contentEl.querySelector('.chat-feedback-comment')) return;

        const box = document.createElement('div');
        box.className = 'chat-feedback-comment';
        box.innerHTML = `
            <textarea rows="2" maxlength="2000" placeholder="O que estava errado? (opcional)"></textarea>
            <div class="chat-feedback-comment-actions">
                <button type="button" class="button outline small chat-feedback-dismiss">Dispensar</button>
                <button type="button" class="button small chat-feedback-send">Enviar</button>
            </div>
        `;

        const textarea = box.querySelector('textarea') as HTMLTextAreaElement;
        const sendBtn = box.querySelector('.chat-feedback-send') as HTMLButtonElement;
        const dismissBtn = box.querySelector('.chat-feedback-dismiss') as HTMLButtonElement;

        dismissBtn.addEventListener('click', () => box.remove());
        sendBtn.addEventListener('click', async () => {
            const comment = textarea.value.trim();
            if (!comment) {
                box.remove();
                return;
            }
            sendBtn.disabled = true;
            try {
                await this.sendFeedback(messageId, 'dislike', comment);
                box.remove();
                window.$events.success('Obrigado pelo feedback!');
            } catch (error) {
                sendBtn.disabled = false;
                window.$events.error('Erro ao enviar feedback.');
            }
        });

        contentEl.appendChild(box);
        textarea.focus();
    }

    private removeFeedbackCommentBox(dislikeBtn: HTMLElement): void {
        dislikeBtn.closest('.chat-message-content')?.querySelector('.chat-feedback-comment')?.remove();
    }

    private showHistoryLoading(): void {
        if (!this.body.querySelector('.history-loading-container')) {
            const clone = this.historyLoadingTemplate.content.cloneNode(true) as DocumentFragment;
            this.body.prepend(clone);
        }
    }

    private hideHistoryLoading(): void {
        this.body.querySelector('.history-loading-container')?.remove();
    }

    private openDocModal(targetUrl: string): void {
        let modal = document.getElementById('chat-doc-iframe-modal') as HTMLDialogElement;
        if (!modal) {
            modal = document.createElement('dialog');
            modal.id = 'chat-doc-iframe-modal';
            modal.className = 'chat-doc-modal';

            modal.innerHTML = `
                <div class="chat-doc-modal-container">
                    <div class="chat-doc-modal-header">
                        <h3 class="chat-doc-modal-title">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                            Visualização de Documento
                        </h3>
                        <button type="button" class="modal-close-btn">&times;</button>
                    </div>
                    <div class="chat-doc-modal-body">
                        <div class="modal-loader">
                            <div class="preview-spinner"></div>Carregando documento...
                        </div>
                        <iframe id="chat-doc-iframe" src=""></iframe>
                    </div>
                    <div class="chat-doc-modal-footer">
                        <a id="chat-doc-full-link" href="#" target="_blank" class="button outline">Abrir na Athena</a>
                        <button type="button" class="button modal-close-btn">Fechar</button>
                    </div>
                </div>
            `;

            document.body.appendChild(modal);

            const closeBtns = modal.querySelectorAll('.modal-close-btn');
            closeBtns.forEach(btn => btn.addEventListener('click', () => {
                modal.close();
                document.body.style.overflow = '';
            }));

            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    modal.close();
                    document.body.style.overflow = '';
                }
            });
        }

        // Parse slugs to form the iframe url (/books/{bookSlug}/page/{pageSlug})
        // Assuming targetUrl is the full URL or relative like /books/foo/page/bar
        let iframeUrl = targetUrl;
        if (targetUrl.includes('/books/') && targetUrl.includes('/page/')) {
            const matches = targetUrl.match(/\/books\/([^\/]+)\/page\/([^\/?#]+)/);
            if (matches && matches.length === 3) {
                iframeUrl = `/chat/doc-modal/${matches[1]}/page/${matches[2]}`;
            }
        }

        const iframe = modal.querySelector('#chat-doc-iframe') as HTMLIFrameElement;
        const loader = modal.querySelector('.modal-loader') as HTMLDivElement;
        const fullLink = modal.querySelector('#chat-doc-full-link') as HTMLAnchorElement;

        loader.style.display = 'flex';
        iframe.style.opacity = '0';
        iframe.src = iframeUrl;
        fullLink.href = targetUrl;

        iframe.onload = () => {
            loader.style.display = 'none';
            iframe.style.opacity = '1';
        };

        document.body.style.overflow = 'hidden';
        modal.showModal();
    }

    private showTypingIndicator(text: string = 'Pensando...'): void {
        this.hideTypingIndicator();
        const clone = this.typingIndicatorTemplate.content.cloneNode(true) as DocumentFragment;
        const textElement = (clone.querySelector('.typing-indicator-text') as HTMLElement);
        if (textElement) {
            textElement.textContent = text;
        }
        this.body.appendChild(clone);
        this.scrollToBottom();
    }

    private updateTypingIndicatorText(text: string): void {
        const indicator = this.body.querySelector('.typing-indicator-text') as HTMLElement;
        if (indicator) {
            indicator.textContent = text;
            this.scrollToBottom();
        }
    }

    private hideTypingIndicator(): void {
        this.body.querySelector('.typing-indicator')?.remove();
    }

    private removeWelcomeMessage(): void {
        this.body.querySelector('.chat-box-welcome')?.remove();
    }

    /**
     * Restaura a mensagem de boas-vindas da Athena no chat
     */
    private showWelcomeMessage(): void {
        if (this.body.querySelector('.chat-box-welcome')) return;

        const logoUrl = document.querySelector('.athena-logo-chat')?.getAttribute('src') || '/logo.png';
        const welcome = document.createElement('div');
        welcome.className = 'chat-box-welcome';
        welcome.innerHTML = `
            <div class="text-center">
                <img src="${logoUrl}" alt="Logo" class="athena-logo-chat" style="width: 300px; height: 120px; opacity: 0.7;">
                <h2 class="mt-l text-muted">Olá, eu sou a Athena.</h2>
                <p class="text-muted">
                    Sou sua assistente de conhecimento. Analiso toda a base para responder suas dúvidas.<br>
                    <strong>O que vamos construir hoje?</strong>
                </p>
            </div>
        `;
        this.body.prepend(welcome);
    }

    /**
     * Exibe o loading inicial (reutiliza o existente do template)
     */
    private showInitialLoading(): void {
        if (this.initialLoading) {
            this.initialLoading.style.display = 'flex';
            this.initialLoading.style.opacity = '1';
        }
    }

    private handleInput(): void {
        this.input.style.height = 'auto';
        this.input.style.height = `${Math.min(this.input.scrollHeight, 150)}px`;
        this.saveDraftDebounced();
    }

    private draftKey(conversationId: number | null = this.currentConversationId): string {
        return `athena-chat-draft:${conversationId ?? 'new'}`;
    }

    private saveDraftDebounced(): void {
        if (this.draftSaveTimer !== null) {
            window.clearTimeout(this.draftSaveTimer);
        }
        this.draftSaveTimer = window.setTimeout(() => {
            this.draftSaveTimer = null;
            this.flushDraftNow();
        }, 300);
    }

    private flushDraftNow(key: string = this.draftKey()): void {
        try {
            const text = this.input.innerText.trim();
            if (text) {
                localStorage.setItem(key, text);
            } else {
                localStorage.removeItem(key);
            }
        } catch (e) { /* localStorage indisponível — rascunho é best-effort */ }
    }

    private restoreDraft(): void {
        try {
            const draft = localStorage.getItem(this.draftKey());
            this.input.innerText = draft || '';
            this.input.style.height = 'auto';
            this.input.style.height = `${Math.min(this.input.scrollHeight, 150)}px`;
        } catch (e) { /* localStorage indisponível */ }
    }

    private clearDraft(conversationId: number | null = this.currentConversationId): void {
        if (this.draftSaveTimer !== null) {
            window.clearTimeout(this.draftSaveTimer);
            this.draftSaveTimer = null;
        }
        try {
            localStorage.removeItem(this.draftKey(conversationId));
        } catch (e) { /* localStorage indisponível */ }
    }

    private handleKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.handleSubmit(new Event('submit'));
        }
    }

    /**
     * Inicializa sidebar se o componente existir no DOM
     */
    private initializeSidebar(): void {
        const sidebarElement = document.querySelector('[component="chat-sidebar"]');
        if (!sidebarElement) return;

        sidebarElement.addEventListener('chat-sidebar-conversation-selected', ((e: CustomEvent) => {
            this.switchConversation(e.detail.id);
        }) as EventListener);

        sidebarElement.addEventListener('chat-sidebar-conversation-deleted', ((e: CustomEvent) => {
            this.clearDraft(e.detail.id);
            if (this.currentConversationId === e.detail.id) {
                this.clearMessages();
                this.currentConversationId = null;
                this.showWelcomeMessage();
                this.restoreDraft();
            }
        }) as EventListener);

        const tryGetComponent = () => {
            const comp = (sidebarElement as any).__component;
            if (comp) {
                this.sidebar = comp;
            }
        };

        tryGetComponent();
        if (!this.sidebar) {
            setTimeout(tryGetComponent, 100);
        }
    }

    /**
     * Retorna o guest session ID do localStorage, criando um novo se não existir.
     * Usado como fallback quando o cookie não está disponível (HTTP dev, cookie perdido, etc).
     * Sempre enviado como header X-Chat-Session-Id para que o servidor identifique o guest.
     */
    private getOrCreateGuestSessionId(): string {
        return ChatConversationService.getOrCreateGuestSessionId();
    }

    /**
     * Troca para outra conversa
     */
    private async switchConversation(conversationId: number): Promise<void> {
        if (this.currentConversationId === conversationId) {
            return;
        }

        // Preserva o que estava sendo digitado na conversa que está sendo deixada.
        this.flushDraftNow();

        // Se há um stream ativo, aborta e sinaliza para o catch handler mostrar o badge.
        if (this.isStreaming && this.abortController) {
            this.prevStreamingConversationId = this.currentConversationId;
            this.abortedBySwitch = true;
            this.abortController.abort();
        }

        this.currentConversationId = conversationId;
        this.autoScrollPinned = true;
        this.clearMessages();

        // Reseta estado de paginação — fetchAndRenderHistory vai definir os valores corretos
        this.hasMoreHistory = false;
        this.firstMessageId = null;
        this.isLoadingHistory = false;

        this.showInitialLoading();

        try {
            // Reutiliza o mesmo fluxo do load inicial: seta hasMoreHistory e firstMessageId corretamente,
            // evitando o bug de duplicação que ocorria quando hasMoreHistory ficava true sem firstMessageId
            await this.fetchAndRenderHistory();
        } finally {
            this.hideInitialLoading();
            this.restoreDraft();
        }
    }

    /**
     * Limpa todas as mensagens do chat
     */
    private clearMessages(): void {
        const messages = this.body.querySelectorAll('.chat-message-container');
        messages.forEach(msg => msg.remove());
        this.removeWelcomeMessage();
    }

    // Sem force, respeita o auto-scroll: não puxa de volta quem rolou para cima.
    private scrollToBottom(force: boolean = false): void {
        if (!force && !this.autoScrollPinned) return;

        this.autoScrollPinned = true;
        this.jumpBottomBtn?.classList.add('hidden');
        this.body.scrollTop = this.body.scrollHeight;
    }

    private createJumpToBottomButton(): void {
        if (this.jumpBottomBtn || !this.body.parentElement) return;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chat-jump-bottom hidden';
        btn.title = 'Voltar ao fim da conversa';
        btn.setAttribute('aria-label', 'Voltar ao fim da conversa');
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"></path><path d="m19 12-7 7-7-7"></path></svg>`;
        btn.addEventListener('click', () => this.scrollToBottom(true));

        this.body.parentElement.appendChild(btn);
        this.jumpBottomBtn = btn;
    }

    private toggleStopButton(showStop: boolean): void {
        if (this.stopButton && this.sendButton) {
            this.stopButton.style.display = showStop ? 'flex' : 'none';
            this.sendButton.style.display = showStop ? 'none' : 'flex';
        }
    }

    public handleStopGeneration(event?: Event): void {
        if (event) event.preventDefault();
        if (this.abortController) this.abortController.abort();
        // isStreaming será resetado no finally do handleSubmit
        this.toggleStopButton(false);
        this.hideTypingIndicator();
    }
}
